import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import Switch from '@ingka/switch';

import {
  KompisSheet,
  KompisSheetHeader,
  KompisSheetBody,
  KompisSheetBodyPadding,
} from '@inter-ikea-kompis/react-components';
import {
  KompisHyperlink,
  KompisText,
} from '@inter-ikea-kompis/react-components';
import {
  HyperlinkColorEnum,
  HyperlinkTargetEnum,
} from '@inter-ikea-kompis/component-hyperlink';
import { SheetSizeEnum } from '@inter-ikea-kompis/component-sheet';

import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { ProductUtility } from '@inter-ikea-kompis/utilities';

import styles from './MountingRailSwitch.module.less';
import { IconButton } from '../IconButton';
import { ButtonTypeEnum } from '@inter-ikea-kompis/component-button';
import { IconButtonSizeEnum } from '@inter-ikea-kompis/component-icon-button';

import Portal from '../utils/Portal';
import productService from '../../services/products';
import articles from '../../services/products/articles';
import { translate } from '../../services/L10n';
import constants from '../../settings/constants';
import platform from '../../util/platform';
import { mountingRailSheetOpened } from '../../state/popups/popupsActions';
import { selectDexfSettings } from '../../state/dexfSettings/dexfSettingsSelectors';
import { selectHasShownMountingRailSheet } from '../../state/popups/popupsSelectors';
import { actionSetDirtyConfiguration } from '../../state/vpc/vpcActions';
import localStatisticsReporter from '../../services/statistics/insights/custom/local/localStatisticsReporter';
import { t } from '../../translations';

const MountingRailSwitch = ({
  onChange,
  checked,
  isMobile,
  isLandscape,
  hasShownSheet,
  setHasShownSheet,
  dexfSettings,
}) => {
  if (!productService.areMountingRailsValid()) {
    return null;
  }

  const getAllMountingRailIds = () => {
    const colors = Object.keys(constants.MOUNTING_RAILS);
    const allMountingRailIds = colors.reduce(
      (mountingRailIds, color) => [
        ...mountingRailIds,
        ...Object.values(constants.MOUNTING_RAILS[color]),
      ],
      []
    );
    return allMountingRailIds;
  };

  const createPipLink = (mountingRailIds, dexfSettings) => {
    for (const id of mountingRailIds) {
      const article = articles.getArticle(id);

      const pipUrl =
        article &&
        ProductUtility.getProductInformationPageLink(article, dexfSettings);
      if (pipUrl) {
        return pipUrl;
      }
    }
  };

  const [showSheet, setShowSheet] = useState(false);
  const allMountingRailIds = getAllMountingRailIds();
  const pipLink = createPipLink(allMountingRailIds, dexfSettings);
  const dispatch = useDispatch();

  const handleOnChange = toggleSheet => {
    if (toggleSheet) {
      onChange(checked);
      dispatch(actionSetDirtyConfiguration(true));
    }
    if (!hasShownSheet || !toggleSheet) {
      setShowSheet(true);
      setHasShownSheet(true);
    }
  };

  const handleHelpIconClick = () => {
    localStatisticsReporter.reportProductMenuMountingRailInfoClicked();
    handleOnChange();
  };

  const renderHelpIcon = (
    <IconButton
      iconName="QuestionMarkCircleIcon"
      size={IconButtonSizeEnum.xsmall}
      type={ButtonTypeEnum.tertiary}
      onClick={handleHelpIconClick}
    />
  );

  return (
    <React.Fragment>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <label onClick={() => handleOnChange(true)}>
            {translate(t.MOUNTING_RAIL)}
          </label>
          {!isMobile && renderHelpIcon}
        </div>
        <div className={styles.switchRow}>
          <Switch
            id="mounting-rail-switch"
            value={`${checked}`}
            checked={checked}
            subtle
            onChange={() => handleOnChange(true)}
          />
          {isMobile && renderHelpIcon}
        </div>
      </div>
      <Portal>
        <KompisSheet
          onOverlayClick={() => {
            setShowSheet(false);
          }}
          visible={showSheet}
          theme={SkapaTheme}
          sheetSize={SheetSizeEnum.small}
        >
          <KompisSheetHeader
            floatingHeader={isMobile && isLandscape}
            theme={SkapaTheme}
            heading={''}
            onCloseButtonClick={() => {
              setShowSheet(false);
            }}
          />
          <KompisSheetBody className={styles.sheetBody} theme={SkapaTheme}>
            <KompisSheetBodyPadding theme={SkapaTheme}>
              <div className={styles.infoContainer}>
                <p className={styles.articleName}>
                  {translate(t.MOUNTING_RAIL)}
                </p>
                <h2 className={styles.title}>{constants.APPLICATION_NAME}</h2>
                <p>
                  {translate(
                    t.MOUNTING_RAIL_HELPS_YOU_HANG_STORAGE_RAILS_EVENLY
                  )}
                </p>
                <p>
                  {translate(t.MOUNTING_RAIL_COMES_IN_THREE_DIFFERENT_LENGTHS)}{' '}
                  {translate(t.MOUNTING_RAIL_CUT)}
                </p>
                {!platform.isKiosk && (
                  <KompisText theme={SkapaTheme}>
                    <KompisHyperlink
                      href={pipLink}
                      target={HyperlinkTargetEnum.blank}
                      text={translate(t.INFORMATION_VIEW_MORE_INFORMATION)}
                      color={HyperlinkColorEnum.black}
                      theme={SkapaTheme}
                      underline={true}
                    />
                  </KompisText>
                )}
              </div>
              <div className={styles.imageContainer}>
                <img
                  alt=""
                  src={`${constants.IMAGE_ROOT}${constants.MOUNTING_RAIL_SAFETY_INSTRUCTIONS}`}
                />
              </div>
            </KompisSheetBodyPadding>
          </KompisSheetBody>
        </KompisSheet>
      </Portal>
    </React.Fragment>
  );
};

MountingRailSwitch.propTypes = {
  onChange: PropTypes.func.isRequired,
  checked: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  isLandscape: PropTypes.bool.isRequired,
  hasShownSheet: PropTypes.bool.isRequired,
  setHasShownSheet: PropTypes.func.isRequired,
};

export default connect(
  state => ({
    hasShownSheet: selectHasShownMountingRailSheet(state),
    dexfSettings: selectDexfSettings(state),
  }),
  dispatch => ({
    setHasShownSheet: () => dispatch(mountingRailSheetOpened()),
  })
)(MountingRailSwitch);
